import { useState, type FormEvent } from 'react'
import type { AdminUser } from '../lib/api'
import { Spinner } from './Spinner'

export type CredentialsPatch = { email?: string; password?: string }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The credential half of `PATCH /api/admin/users/:id` (§4, amended 2026-10-09): an admin sets a new
 * address, a new password, or both on someone's account — and, because the api allows the caller's
 * own address and password (only a change to their own status or admin flag is refused), this is
 * also how the owner resets their own password. Both fields are optional; at least one is required.
 *
 * The form is step one, the confirm line is step two: nothing is sent until the values are shown
 * back and confirmed, the same shape as the delete flow.
 */
export function CredentialsForm({
  user,
  busy,
  onSave,
  onCancel,
}: {
  user: AdminUser
  busy: boolean
  onSave: (patch: CredentialsPatch) => void
  onCancel: () => void
}) {
  const [email, setEmail] = useState(user.email ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<CredentialsPatch | null>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const nextEmail = email.trim()
    // The prefilled address is the current one: re-sending it would be a no-op write, and the api
    // treats an address that belongs to another account as a conflict. Only a change is sent.
    const emailChanged = nextEmail !== '' && nextEmail !== (user.email ?? '')
    if (!emailChanged && password === '') {
      setError('Enter a new email, a new password, or both.')
      return
    }
    if (emailChanged && (nextEmail.length > 254 || !EMAIL_PATTERN.test(nextEmail))) {
      setError('That does not look like an email address.')
      return
    }
    if (password !== '' && (password.length < 8 || password.length > 200)) {
      setError('A password is 8 to 200 characters.')
      return
    }
    setError(null)
    setConfirming({
      email: emailChanged ? nextEmail : undefined,
      password: password === '' ? undefined : password,
    })
  }

  if (confirming) {
    const what =
      confirming.email !== undefined && confirming.password !== undefined
        ? 'a new email and a new password'
        : confirming.email !== undefined
          ? `the email ${confirming.email}`
          : 'a new password'
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-meta text-ink">
          Set {what} for <bdi>{user.displayName}</bdi>?
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => onSave(confirming)}
          className="press flex min-h-11 items-center gap-2 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
        >
          {busy && <Spinner size={16} label="Saving credentials" />}
          Confirm
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(null)}
          className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-50"
        >
          Back
        </button>
      </span>
    )
  }

  return (
    <form onSubmit={submit} className="flex w-full flex-wrap items-end gap-2">
      <label className="flex min-w-40 flex-1 flex-col gap-1 text-meta text-muted">
        New email
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder={user.email ?? 'name@example.com'}
          className="min-h-11 rounded-panel bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset placeholder:text-muted"
        />
      </label>
      <label className="flex min-w-40 flex-1 flex-col gap-1 text-meta text-muted">
        New password
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          className="min-h-11 rounded-panel bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset"
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="press min-h-11 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
      >
        Save credentials
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
      >
        Cancel
      </button>
      {error && (
        <p role="alert" className="w-full text-meta text-danger">
          {error}
        </p>
      )}
    </form>
  )
}
